import { useState } from "react";
import { Frame, useWidgetText } from "../shared";
import type { WidgetProps } from "../types";
import { controlBindings, controlState, isPending } from "./control";
import { ControlNotes } from "./ControlNotes";

/** slider: a level (brightness, a setpoint) within the widget's limits, never wider than the profile's. */
export function Slider({ widget, input, onAction, readOnly }: WidgetProps) {
  const fmt = useWidgetText();
  const { target, targetKey, action, point, device } = controlBindings(widget, input);
  const reported = point?.latest?.value;
  const pending = isPending(action);
  const shown = pending && typeof action?.value === "number" ? action.value : reported;
  const [drag, setDrag] = useState<number>();
  const offline = device?.state === "offline";
  const state = controlState(action, typeof reported === "number", offline);
  const cfg = widget.config ?? {};
  const pmin = point?.range?.[0] ?? 0;
  const pmax = point?.range?.[1] ?? 100;
  const min = typeof cfg.min === "number" ? cfg.min : pmin;
  const max = typeof cfg.max === "number" ? cfg.max : pmax;
  const step = typeof cfg.step === "number" ? cfg.step : 1;
  const name = action?.name ?? point?.name ?? "";
  const value = drag ?? (typeof shown === "number" ? shown : undefined);
  const enabled = (action?.allowed ?? false) && !pending && !readOnly;
  function commit() {
    if (drag === undefined || !target || !targetKey || !action) return;
    onAction?.({ kind: action.kind, device: target.device, key: targetKey, value: drag });
    setDrag(undefined);
  }
  return (
    <Frame widget={widget} state={state} title={widget.title}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs text-gray-700">{name}</span>
        <span className="font-semibold tabular-nums text-gray-900">
          {value !== undefined && point ? fmt.value(value, point) : fmt.t("state.noData")}
        </span>
      </div>
      <input
        type="range"
        aria-label={name}
        min={min}
        max={max}
        step={step}
        value={value ?? min}
        disabled={!enabled}
        onChange={(e) => setDrag(Number(e.target.value))}
        onPointerUp={commit}
        onKeyUp={commit}
        className="w-full accent-brand disabled:opacity-50"
      />
      <ControlNotes state={state} offline={offline} />
    </Frame>
  );
}
